import React, { useEffect, useMemo, useState } from "react"
import {
    Box, Typography, Paper, CircularProgress, Tabs, Tab,
    Table, TableBody, TableCell, TableHead, TableRow,
    TextField, MenuItem, Autocomplete, Button, IconButton,
    Alert, Chip
} from "@mui/material"
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft"
import ChevronRightIcon from "@mui/icons-material/ChevronRight"
import dayjs from "dayjs"
import { DatePicker } from "@mui/x-date-pickers/DatePicker"
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs"
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider"
import { ptBR as adapterptBR } from "dayjs/locale/pt-br"
import { ptBR as localePTBR } from "@mui/x-date-pickers/locales"
import api from "../../api"

const PAGE_SIZE = 25

const ALERT_TYPES = ["disconnect_threshold", "reconciliation_failed"]
const ALERT_STATUSES = ["open", "resolved"]
const RETRY_SOURCES = ["periodic", "disconnect"]
const RETRY_NUMERIC_FIELDS = [
    { key: "total_retries", label: "Retries" },
    { key: "successful_retries", label: "Sucesso" },
    { key: "failed_retries", label: "Falha" },
    { key: "session_recreations", label: "Sessões" },
    { key: "phone_requests", label: "Ph. Req." },
]
const RECOVERY_STATUSES = ["pendente", "recebido", "expirado"]
const LOG_EVENTS = ["connect", "disconnect", "create", "remove", "qrcode", "reconnect", "add_ignore", "remove_ignore"]

// enterprises/?metrics=1 is an existing, IsSuperAdminUser-permitted lookup used
// elsewhere for lightweight enterprise pickers. It only covers active,
// non-trial enterprises — acceptable for this admin tool, not exhaustive.
const fetchEnterpriseOptions = () =>
    api.get("api/enterprises/?metrics=1", { timeout: 15000 }).then(res => res.data ?? [])

const fetchAccountOptions = (enterpriseId) =>
    api.get("api/superadmin/scout-observability/accounts/", {
        params: { enterprise: enterpriseId || undefined, limit: 200 },
        timeout: 15000,
    }).then(res => res.data?.results ?? [])

const formatDateTime = (value) => value ? dayjs(value).format("DD/MM/YYYY HH:mm:ss") : "-"

const StatusChip = ({ value }) => {
    const color = {
        pendente: "warning", recebido: "success", expirado: "default",
        open: "warning", resolved: "success",
    }[value] || "default"
    return <Chip size="small" label={value} color={color} variant="outlined" />
}

const PagedTable = ({ columns, rows, count, page, setPage, loading, emptyLabel }) => {
    const totalPages = Math.max(1, Math.ceil(count / PAGE_SIZE))
    return (
        <Paper elevation={2} sx={{ borderRadius: 3, overflow: "hidden" }}>
            <Box sx={{ overflowX: "auto" }}>
                <Table size="small">
                    <TableHead>
                        <TableRow sx={{ "& th": { fontWeight: "bold", color: "#21446C", borderBottom: "2px solid #e0e0e0" } }}>
                            {columns.map(col => (
                                <TableCell key={col.key} align={col.align || "left"}>{col.label}</TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={columns.length} align="center" sx={{ py: 3 }}>
                                    <CircularProgress size={20} sx={{ color: "#5399DB" }} />
                                </TableCell>
                            </TableRow>
                        ) : rows.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={columns.length} align="center" sx={{ py: 3, color: "#999", fontStyle: "italic" }}>
                                    {emptyLabel || "Nenhum registro encontrado."}
                                </TableCell>
                            </TableRow>
                        ) : rows.map((row, idx) => (
                            <TableRow key={row.id ?? idx} hover>
                                {columns.map(col => (
                                    <TableCell key={col.key} align={col.align || "left"}>
                                        {col.render ? col.render(row) : (row[col.key] ?? "-")}
                                    </TableCell>
                                ))}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 1, px: 2, py: 1 }}>
                <Typography variant="body2" sx={{ color: "#666" }}>
                    {count} registro{count === 1 ? "" : "s"} — página {page + 1} de {totalPages}
                </Typography>
                <IconButton size="small" disabled={page === 0} onClick={() => setPage(p => Math.max(0, p - 1))}>
                    <ChevronLeftIcon />
                </IconButton>
                <IconButton size="small" disabled={page + 1 >= totalPages} onClick={() => setPage(p => p + 1)}>
                    <ChevronRightIcon />
                </IconButton>
            </Box>
        </Paper>
    )
}

const useSectionData = (endpoint, extraParams) => {
    const [data, setData] = useState({ count: 0, results: [] })
    const [loading, setLoading] = useState(false)
    const [page, setPage] = useState(0)

    const paramsKey = JSON.stringify(extraParams)

    useEffect(() => { setPage(0) }, [paramsKey])

    useEffect(() => {
        let cancelled = false
        setLoading(true)
        api.get(`api/superadmin/scout-observability/${endpoint}/`, {
            params: { ...extraParams, limit: PAGE_SIZE, offset: page * PAGE_SIZE },
            timeout: 15000,
        }).then(res => {
            if (!cancelled) setData(res.data)
        }).catch(() => {
            if (!cancelled) setData({ count: 0, results: [] })
        }).finally(() => {
            if (!cancelled) setLoading(false)
        })
        return () => { cancelled = true }
    }, [endpoint, extraParams, paramsKey, page])

    return { data, loading, page, setPage }
}

const FilterBar = ({ children }) => (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, alignItems: "center", mb: 2 }}>
        {children}
    </Box>
)

const EnterpriseAccountFilters = ({
    enterprises, enterprise, setEnterprise,
    accounts, account, setAccount, accountsLoading,
}) => (
    <>
        <Autocomplete
            size="small"
            options={enterprises}
            getOptionLabel={(opt) => opt?.name || ""}
            isOptionEqualToValue={(opt, val) => opt.id === val.id}
            value={enterprise}
            onChange={(_, val) => setEnterprise(val)}
            sx={{ minWidth: 240 }}
            renderInput={(params) => <TextField {...params} label="Empresa" />}
        />
        <Autocomplete
            size="small"
            options={accounts}
            loading={accountsLoading}
            getOptionLabel={(opt) => opt?.name ? `${opt.name} (${opt.phone_number})` : ""}
            isOptionEqualToValue={(opt, val) => opt.id === val.id}
            value={account}
            onChange={(_, val) => setAccount(val)}
            sx={{ minWidth: 260 }}
            renderInput={(params) => <TextField {...params} label="Conta Scout" />}
        />
    </>
)

const NumberRangeFilter = ({ label, minValue, maxValue, onMinChange, onMaxChange }) => (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        <Typography variant="body2" sx={{ color: "#666", whiteSpace: "nowrap" }}>{label}</Typography>
        <TextField
            size="small" type="number" placeholder="mín" value={minValue}
            onChange={e => onMinChange(e.target.value)} sx={{ width: 80 }}
        />
        <Typography variant="body2" sx={{ color: "#999" }}>–</Typography>
        <TextField
            size="small" type="number" placeholder="máx" value={maxValue}
            onChange={e => onMaxChange(e.target.value)} sx={{ width: 80 }}
        />
    </Box>
)

const PeriodFilters = ({ since, setSince, until, setUntil }) => (
    <>
        <DatePicker
            label="Desde"
            value={since}
            onChange={setSince}
            slotProps={{ textField: { size: "small" }, field: { clearable: true } }}
        />
        <DatePicker
            label="Até"
            value={until}
            onChange={setUntil}
            slotProps={{ textField: { size: "small" }, field: { clearable: true } }}
        />
    </>
)

const ScoutObservability = () => {
    const [enterprises, setEnterprises] = useState([])
    const [accounts, setAccounts] = useState([])
    const [accountsLoading, setAccountsLoading] = useState(false)
    const [enterprise, setEnterprise] = useState(null)
    const [account, setAccount] = useState(null)
    const [since, setSince] = useState(null)
    const [until, setUntil] = useState(null)
    const [tab, setTab] = useState(0)

    const [alertType, setAlertType] = useState("")
    const [alertStatus, setAlertStatus] = useState("")
    const [retrySource, setRetrySource] = useState("")
    const [retryNumericFilters, setRetryNumericFilters] = useState({})
    const [recoveryStatus, setRecoveryStatus] = useState("")
    const [logEvent, setLogEvent] = useState("")

    useEffect(() => {
        fetchEnterpriseOptions().then(setEnterprises).catch(() => setEnterprises([]))
    }, [])

    useEffect(() => {
        setAccount(null)
        setAccountsLoading(true)
        fetchAccountOptions(enterprise?.id)
            .then(setAccounts)
            .catch(() => setAccounts([]))
            .finally(() => setAccountsLoading(false))
    }, [enterprise])

    const baseParams = useMemo(() => ({
        enterprise: enterprise?.id || undefined,
        scout_account: account?.id || undefined,
        since: since ? since.startOf("day").toISOString() : undefined,
        until: until ? until.endOf("day").toISOString() : undefined,
    }), [enterprise, account, since, until])

    const alertsParams = useMemo(() => ({ ...baseParams, type: alertType || undefined, status: alertStatus || undefined }), [baseParams, alertType, alertStatus])
    const bucketsParams = baseParams
    const setRetryNumericValue = (paramKey, value) =>
        setRetryNumericFilters(prev => ({ ...prev, [paramKey]: value }))

    const retryParams = useMemo(() => {
        const numeric = {}
        RETRY_NUMERIC_FIELDS.forEach(({ key }) => {
            const min = retryNumericFilters[`${key}_min`]
            const max = retryNumericFilters[`${key}_max`]
            if (min !== undefined && min !== "") numeric[`${key}_min`] = min
            if (max !== undefined && max !== "") numeric[`${key}_max`] = max
        })
        return { ...baseParams, source: retrySource || undefined, ...numeric }
    }, [baseParams, retrySource, retryNumericFilters])
    const recoveryParams = useMemo(() => ({ ...baseParams, status: recoveryStatus || undefined }), [baseParams, recoveryStatus])
    const logsParams = useMemo(() => ({ ...baseParams, event: logEvent || undefined }), [baseParams, logEvent])

    const alerts = useSectionData("alerts", alertsParams)
    const buckets = useSectionData("disconnect_buckets", bucketsParams)
    const retryStats = useSectionData("retry_stats", retryParams)
    const recoveryRequests = useSectionData("recovery_requests", recoveryParams)
    const logs = useSectionData("logs", logsParams)

    return (
        <LocalizationProvider
            dateAdapter={AdapterDayjs}
            adapterLocale={adapterptBR}
            localeText={localePTBR.components.MuiLocalizationProvider.defaultProps.localeText}
        >
            <Box sx={{ p: 4, display: "flex", flexDirection: "column", gap: 2, height: "100%", overflowY: "auto" }}>
                <Typography variant="h5" fontWeight="bold" sx={{ color: "#21446C" }}>
                    Observabilidade Scout
                </Typography>

                <FilterBar>
                    <EnterpriseAccountFilters
                        enterprises={enterprises} enterprise={enterprise} setEnterprise={setEnterprise}
                        accounts={accounts} account={account} setAccount={setAccount} accountsLoading={accountsLoading}
                    />
                    <PeriodFilters since={since} setSince={setSince} until={until} setUntil={setUntil} />
                </FilterBar>

                <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: "1px solid #e0e0e0" }}>
                    <Tab label="Alertas" />
                    <Tab label="Desconexões" />
                    <Tab label="Retry Stats" />
                    <Tab label="Recuperações" />
                    <Tab label="Logs" />
                    <Tab label="Disparar Recuperação" />
                </Tabs>

                {tab === 0 && (
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                        <FilterBar>
                            <TextField select size="small" label="Tipo" value={alertType} onChange={e => setAlertType(e.target.value)} sx={{ minWidth: 200 }}>
                                <MenuItem value="">Todos</MenuItem>
                                {ALERT_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                            </TextField>
                            <TextField select size="small" label="Status" value={alertStatus} onChange={e => setAlertStatus(e.target.value)} sx={{ minWidth: 160 }}>
                                <MenuItem value="">Todos</MenuItem>
                                {ALERT_STATUSES.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                            </TextField>
                        </FilterBar>
                        <PagedTable
                            {...alerts}
                            rows={alerts.data.results}
                            count={alerts.data.count}
                            columns={[
                                { key: "scout_account_id", label: "Conta" },
                                { key: "type", label: "Tipo" },
                                { key: "status", label: "Status", render: (r) => <StatusChip value={r.status} /> },
                                { key: "created_at", label: "Criado em", render: (r) => formatDateTime(r.created_at) },
                                { key: "window_ref", label: "Janela" },
                                { key: "details", label: "Detalhes" },
                            ]}
                        />
                    </Box>
                )}

                {tab === 1 && (
                    <PagedTable
                        {...buckets}
                        rows={buckets.data.results}
                        count={buckets.data.count}
                        columns={[
                            { key: "scout_account_id", label: "Conta" },
                            { key: "reason", label: "Motivo" },
                            { key: "bucket_hour", label: "Hora", render: (r) => formatDateTime(r.bucket_hour) },
                            { key: "count", label: "Qtd.", align: "right" },
                        ]}
                    />
                )}

                {tab === 2 && (
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                        <FilterBar>
                            <TextField select size="small" label="Origem" value={retrySource} onChange={e => setRetrySource(e.target.value)} sx={{ minWidth: 180 }}>
                                <MenuItem value="">Todas</MenuItem>
                                {RETRY_SOURCES.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                            </TextField>
                            {RETRY_NUMERIC_FIELDS.map(({ key, label }) => (
                                <NumberRangeFilter
                                    key={key}
                                    label={label}
                                    minValue={retryNumericFilters[`${key}_min`] ?? ""}
                                    maxValue={retryNumericFilters[`${key}_max`] ?? ""}
                                    onMinChange={(v) => setRetryNumericValue(`${key}_min`, v)}
                                    onMaxChange={(v) => setRetryNumericValue(`${key}_max`, v)}
                                />
                            ))}
                        </FilterBar>
                        <PagedTable
                            {...retryStats}
                            rows={retryStats.data.results}
                            count={retryStats.data.count}
                            columns={[
                                { key: "scout_account_id", label: "Conta" },
                                { key: "captured_at", label: "Capturado em", render: (r) => formatDateTime(r.captured_at) },
                                { key: "connection_state", label: "Estado" },
                                { key: "source", label: "Origem" },
                                { key: "total_retries", label: "Retries", align: "right" },
                                { key: "successful_retries", label: "Sucesso", align: "right" },
                                { key: "failed_retries", label: "Falha", align: "right" },
                                { key: "session_recreations", label: "Sessões", align: "right" },
                                { key: "phone_requests", label: "Ph. Req.", align: "right" },
                            ]}
                        />
                    </Box>
                )}

                {tab === 3 && (
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                        <FilterBar>
                            <TextField select size="small" label="Status" value={recoveryStatus} onChange={e => setRecoveryStatus(e.target.value)} sx={{ minWidth: 180 }}>
                                <MenuItem value="">Todos</MenuItem>
                                {RECOVERY_STATUSES.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                            </TextField>
                        </FilterBar>
                        <PagedTable
                            {...recoveryRequests}
                            rows={recoveryRequests.data.results}
                            count={recoveryRequests.data.count}
                            columns={[
                                { key: "id", label: "ID" },
                                { key: "scout_account_id", label: "Conta" },
                                { key: "remote_jid", label: "Conversa" },
                                { key: "count", label: "Qtd. Solicitada", align: "right" },
                                { key: "status", label: "Status", render: (r) => <StatusChip value={r.status} /> },
                                { key: "origin", label: "Origem" },
                                { key: "requested_at", label: "Solicitado em", render: (r) => formatDateTime(r.requested_at) },
                                { key: "fulfilled_at", label: "Concluído em", render: (r) => formatDateTime(r.fulfilled_at) },
                                { key: "message_count", label: "Msgs. Recuperadas", align: "right" },
                            ]}
                        />
                    </Box>
                )}

                {tab === 4 && (
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                        <FilterBar>
                            <TextField select size="small" label="Evento" value={logEvent} onChange={e => setLogEvent(e.target.value)} sx={{ minWidth: 180 }}>
                                <MenuItem value="">Todos</MenuItem>
                                {LOG_EVENTS.map(e => <MenuItem key={e} value={e}>{e}</MenuItem>)}
                            </TextField>
                        </FilterBar>
                        <PagedTable
                            {...logs}
                            rows={logs.data.results}
                            count={logs.data.count}
                            columns={[
                                { key: "enterprise_id", label: "Empresa" },
                                { key: "phone_number", label: "Telefone" },
                                { key: "name", label: "Nome" },
                                { key: "event", label: "Evento" },
                                { key: "created_at", label: "Data", render: (r) => formatDateTime(r.created_at) },
                                { key: "details", label: "Detalhes" },
                            ]}
                        />
                    </Box>
                )}

                {tab === 5 && (
                    <TriggerRecoveryPanel
                        enterprises={enterprises}
                        enterprise={enterprise}
                        setEnterprise={setEnterprise}
                        accounts={accounts}
                        account={account}
                        setAccount={setAccount}
                        accountsLoading={accountsLoading}
                    />
                )}
            </Box>
        </LocalizationProvider>
    )
}

const TriggerRecoveryPanel = ({ enterprises, enterprise, setEnterprise, accounts, account, setAccount, accountsLoading }) => {
    const [remoteJid, setRemoteJid] = useState("")
    const [count, setCount] = useState(50)
    const [submitting, setSubmitting] = useState(false)
    const [result, setResult] = useState(null)
    const [error, setError] = useState("")
    const [statusChecking, setStatusChecking] = useState(false)
    const [status, setStatus] = useState(null)

    const canSubmit = enterprise && account && remoteJid.trim() && !submitting

    const handleSubmit = () => {
        setSubmitting(true)
        setError("")
        setResult(null)
        setStatus(null)
        api.post("api/superadmin/scout-observability/recovery/", {
            enterprise_id: enterprise.id,
            scout_account_id: account.id,
            remote_jid: remoteJid.trim(),
            count: Number(count) || 50,
        }, { timeout: 30000 })
            .then(res => setResult(res.data))
            .catch(err => setError(err?.response?.data?.message || err?.response?.data || "Falha ao disparar recuperação."))
            .finally(() => setSubmitting(false))
    }

    const handleCheckStatus = () => {
        if (!result?.id) return
        setStatusChecking(true)
        api.get(`api/superadmin/scout-observability/recovery_status/${result.id}/`, { timeout: 15000 })
            .then(res => setStatus(res.data))
            .catch(() => setStatus(null))
            .finally(() => setStatusChecking(false))
    }

    return (
        <Paper elevation={2} sx={{ borderRadius: 3, p: 3, maxWidth: 560, display: "flex", flexDirection: "column", gap: 2 }}>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ color: "#21446C" }}>
                Disparar recuperação manual de conversa
            </Typography>
            <EnterpriseAccountFilters
                enterprises={enterprises} enterprise={enterprise} setEnterprise={setEnterprise}
                accounts={accounts} account={account} setAccount={setAccount} accountsLoading={accountsLoading}
            />
            <TextField
                size="small" label="remote_jid (ex: 5521999999999@s.whatsapp.net)"
                value={remoteJid} onChange={e => setRemoteJid(e.target.value)}
            />
            <TextField
                size="small" label="Quantidade" type="number" value={count}
                onChange={e => setCount(e.target.value)} sx={{ maxWidth: 160 }}
            />
            <Button variant="contained" disabled={!canSubmit} onClick={handleSubmit}>
                {submitting ? <CircularProgress size={20} sx={{ color: "#fff" }} /> : "Disparar recuperação"}
            </Button>

            {error && <Alert severity="error">{typeof error === "string" ? error : JSON.stringify(error)}</Alert>}

            {result && (
                <Alert severity="success" sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                    <span>Solicitação criada — ID {result.id}, peerDataRequestSessionId: {result.peerDataRequestSessionId}</span>
                    <Button size="small" variant="outlined" onClick={handleCheckStatus} disabled={statusChecking} sx={{ alignSelf: "flex-start" }}>
                        {statusChecking ? "Verificando..." : "Verificar status"}
                    </Button>
                    {status && (
                        <span>
                            Status: <StatusChip value={status.status} /> — Msgs. recuperadas: {status.messageCount ?? "-"}
                        </span>
                    )}
                </Alert>
            )}
        </Paper>
    )
}

export default ScoutObservability
